import React, { useState, useEffect } from 'react';
import { Reservation } from '../../types';
import Modal from '../shared/Modal';
import { checkInReservation, checkOutReservation, parseSpecialRequests } from '../../services/reservationService';
import {
  fetchPaymentsForReservation,
  updatePaymentStatus,
  PaymentRecord,
} from '../../services/paymentService';
import { formatCurrency, formatStatus } from '../../utils/formatters';
import { authService } from '../../services/auth';
import './CheckInOutModal.css';

interface CheckInOutModalProps {
  reservation: Reservation;
  mode: 'checkin' | 'checkout';
  siteId?: number;
  siteAlias?: string;
  onClose: () => void;
  onSuccess: () => void;
}

type CollectMethod = 'cash' | 'check' | 'card_terminal';

interface CollectState {
  collected: boolean;
  method: CollectMethod;
  terminalRef: string;
}

const COLLECT_METHOD_LABELS: Record<CollectMethod, string> = {
  cash: 'Cash',
  check: 'Check',
  card_terminal: 'Card Terminal',
};

const defaultCollectMethod = (payment: PaymentRecord): CollectMethod => {
  if (payment.payment_method === 'check' || payment.payment_method === 'card_terminal') {
    return payment.payment_method;
  }
  return 'cash';
};

const CheckInOutModal: React.FC<CheckInOutModalProps> = ({
  reservation,
  mode,
  siteId,
  siteAlias,
  onClose,
  onSuccess,
}) => {
  const [notes, setNotes] = useState('');
  const parsedData = parseSpecialRequests(reservation.specialRequests);
  const [flagged, setFlagged] = useState(parsedData.flagged || false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pending manual payments to optionally collect at check-in
  const [pendingPayments, setPendingPayments] = useState<PaymentRecord[]>([]);
  const [collectState, setCollectState] = useState<Record<string, CollectState>>({});
  // Set once the check-in itself succeeded, so a retry after a failed payment
  // update doesn't record a second check-in
  const [checkInDone, setCheckInDone] = useState(false);

  const isCheckIn = mode === 'checkin';
  const title = isCheckIn ? 'Check In Guest' : 'Check Out Guest';

  useEffect(() => {
    if (!isCheckIn) return;
    const storeId = authService.getStoreId();
    if (!storeId) return;

    const fetches = [fetchPaymentsForReservation(reservation.id, storeId)];
    if (reservation.originalReservationId) {
      fetches.push(fetchPaymentsForReservation(reservation.originalReservationId, storeId));
    }
    Promise.all(fetches)
      .then((results) => {
        const merged = results.flat();
        const unique = Array.from(new Map(merged.map((p) => [p.id, p])).values());
        const pending = unique.filter(
          (p) => p.gateway_provider === 'manual' && p.status === 'pending'
        );
        setPendingPayments(pending);
        setCollectState(
          Object.fromEntries(
            pending.map((p) => [
              p.id,
              { collected: false, method: defaultCollectMethod(p), terminalRef: '' },
            ])
          )
        );
      })
      .catch((err) => {
        // Payment info is a convenience here — never block check-in on it
        console.error('Payment fetch error:', err);
      });
  }, [isCheckIn, reservation.id, reservation.originalReservationId]);

  const updateCollectState = (paymentId: string, patch: Partial<CollectState>) => {
    setCollectState((prev) => ({ ...prev, [paymentId]: { ...prev[paymentId], ...patch } }));
  };

  const collectNotes = (state: CollectState): string => {
    let note = `Collected at check-in — ${COLLECT_METHOD_LABELS[state.method].toLowerCase()}`;
    if (state.method === 'card_terminal' && state.terminalRef.trim()) {
      note += ` ref #${state.terminalRef.trim()}`;
    }
    if (notes.trim()) {
      note += ` — ${notes.trim()}`;
    }
    return note.slice(0, 500);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (isCheckIn) {
        if (!checkInDone) {
          await checkInReservation(
            reservation.id,
            reservation.specialRequests,
            notes.trim() || undefined,
            flagged,
            siteId
          );
          setCheckInDone(true);
        }

        // Mark collected payments as paid (after a successful check-in)
        const toCollect = pendingPayments.filter((p) => collectState[p.id]?.collected);
        const storeId = authService.getStoreId();
        for (const payment of toCollect) {
          try {
            await updatePaymentStatus(
              payment.id,
              { new_status: 'completed', notes: collectNotes(collectState[payment.id]) },
              storeId ?? payment.store_id
            );
          } catch (err: any) {
            setError(
              `Guest checked in, but marking the ${formatCurrency(payment.amount)} payment as paid failed: ` +
              `${err.message || 'unknown error'}. Retry below or use Payment Details later.`
            );
            setSubmitting(false);
            return;
          }
        }
      } else {
        await checkOutReservation(
          reservation.id,
          reservation.specialRequests,
          notes.trim() || undefined,
          flagged,
          siteId
        );
      }
      // Auto-close on success
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${isCheckIn ? 'check in' : 'check out'}`);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // If the check-in itself already succeeded, make sure the parent refreshes
    if (checkInDone) {
      onSuccess();
    }
    onClose();
  };

  const submitLabel = submitting
    ? 'SAVING...'
    : checkInDone
      ? 'RETRY PAYMENT UPDATE'
      : isCheckIn
        ? 'CHECK IN'
        : 'CHECK OUT';

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={handleClose} disabled={submitting}>
        CANCEL
      </button>
      <button
        className="btn btn-blue"
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitLabel}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={true}
      onClose={handleClose}
      title={title}
      size="small"
      footer={footer}
    >
      <div className="checkinout-modal">
        <p className="checkinout-description">
          {isCheckIn
            ? `Record a check-in for ${siteAlias ? `site ${siteAlias} on ` : ''}reservation ${reservation.confirmationCode}.`
            : `Record a check-out for ${siteAlias ? `site ${siteAlias} on ` : ''}reservation ${reservation.confirmationCode}.`
          }
        </p>

        {isCheckIn && pendingPayments.length > 0 && (
          <div className="checkinout-payment-due">
            <div className="checkinout-payment-due__title">Payment Due</div>
            {pendingPayments.map((payment) => {
              const state = collectState[payment.id];
              if (!state) return null;
              return (
                <div key={payment.id} className="checkinout-payment-due__item">
                  <label className="checkinout-payment-due__collect">
                    <input
                      type="checkbox"
                      checked={state.collected}
                      onChange={(e) => updateCollectState(payment.id, { collected: e.target.checked })}
                      disabled={submitting}
                    />
                    <span>
                      <strong>{formatCurrency(payment.amount)}</strong>
                      {' '}({payment.payment_method ? formatStatus(payment.payment_method) : 'Manual'}, pending)
                      {' '}— payment collected, mark as paid
                    </span>
                  </label>
                  {state.collected && (
                    <div className="checkinout-payment-due__method">
                      <label>Collected as</label>
                      <select
                        value={state.method}
                        onChange={(e) => updateCollectState(payment.id, { method: e.target.value as CollectMethod })}
                        disabled={submitting}
                      >
                        {(Object.keys(COLLECT_METHOD_LABELS) as CollectMethod[]).map((m) => (
                          <option key={m} value={m}>{COLLECT_METHOD_LABELS[m]}</option>
                        ))}
                      </select>
                      {state.method === 'card_terminal' && (
                        <input
                          type="text"
                          placeholder="Terminal ref # (optional)"
                          value={state.terminalRef}
                          onChange={(e) => updateCollectState(payment.id, { terminalRef: e.target.value })}
                          disabled={submitting}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <p className="checkinout-payment-due__hint">
              The collection method is recorded in the payment audit notes.
            </p>
          </div>
        )}

        <div className="checkinout-field">
          <label htmlFor="checkinout-notes">Notes (optional)</label>
          <textarea
            id="checkinout-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., 2 additional adults - charge applied"
            disabled={submitting}
            rows={3}
          />
        </div>

        <label className="checkinout-flag-checkbox">
          <input
            type="checkbox"
            checked={flagged}
            onChange={(e) => setFlagged(e.target.checked)}
            disabled={submitting}
          />
          Flag this reservation (show ★ badge on grid)
        </label>

        {error && (
          <p className="checkinout-error">{error}</p>
        )}
      </div>
    </Modal>
  );
};

export default CheckInOutModal;
